// The library: finding assets, reading one, editing one, and moving or deleting a selection.

import { api, withQuery } from '../client'
import type { components, operations } from '../schema'

export type AssetSummary = components['schemas']['AssetSummary']
export type AssetView = components['schemas']['AssetView']
export type AssetType = components['schemas']['AssetType']
export type AssetStatus = components['schemas']['AssetStatus']
export type SearchAssetsRequest = components['schemas']['SearchAssetsRequest']
export type AssetPage = components['schemas']['Page_AssetSummary']
export type UpdateAssetRequest = components['schemas']['UpdateAssetRequest']
export type AssetSlugSuggestion = components['schemas']['AssetSlugSuggestion']
export type MoveAssetsRequest = components['schemas']['MoveAssetsRequest']
export type AssetsMoved = components['schemas']['AssetsMoved']
export type DeleteAssetsRequest = components['schemas']['DeleteAssetsRequest']
export type AssetsDeleted = components['schemas']['AssetsDeleted']
export type AssetDeletionEntry = components['schemas']['AssetDeletionEntry']
export type PreviewAssetDeletionRequest = components['schemas']['PreviewAssetDeletionRequest']
export type AssetDeletionPreview = components['schemas']['AssetDeletionPreview']
export type AssetUsageEntry = components['schemas']['AssetUsageEntry']
export type AssetAncestryEntry = components['schemas']['AssetAncestryEntry']
export type AssetAncestorEntry = components['schemas']['AssetAncestorEntry']
/** The walk: `limit`, `after`, `order`, and `sort` — `created_at` unless asked otherwise. */
export type AssetSearchPage = NonNullable<operations['assets_search']['parameters']['query']>

/**
 * One page of the assets the caller may read that match `criteria`, and the cursor for the next.
 * There is no total: a caller walks `next` until it is `null`. A cursor resumes only the `sort` it
 * was issued under; sending it under the other one is a 400.
 */
export function searchAssets(
  criteria: SearchAssetsRequest,
  page: AssetSearchPage = {},
  signal?: AbortSignal,
): Promise<AssetPage> {
  return api.post<AssetPage>(withQuery('/assets/search', page), criteria, undefined, signal)
}

/** A Scribe project's review state, which `sort=subtitle_activity` narrows on. */
export type SubtitleReviewState = NonNullable<SearchAssetsRequest['subtitle_review']>
export type SubtitleReviewCounts = components['schemas']['SubtitleReviewCounts']

/**
 * How many projects — assets carrying a caption track — `criteria` selects, in all and in each
 * review state: what Scribe's filter menu shows beside each state, whatever page is loaded.
 * `subtitle_review` is not read: every state is counted.
 */
export function countSubtitleReviews(
  criteria: SearchAssetsRequest,
  signal?: AbortSignal,
): Promise<SubtitleReviewCounts> {
  return api.post<SubtitleReviewCounts>('/assets/subtitle-review-counts/query', criteria, undefined, signal)
}

/**
 * One asset, through the placement the caller reads it by — `workspace_id` and `parent_id` are
 * that workspace's — with every tag on it. 404 for an asset the caller cannot read, whatever the
 * reason.
 *
 * `viaAssetId` names an asset the caller reads in the organization's pool: a file linked to it is
 * then read through it, at its home placement, though it sits on a shelf the caller does not see.
 */
export function getAsset(assetId: string, signal?: AbortSignal, viaAssetId?: string): Promise<AssetView> {
  return api.get<AssetView>(
    withQuery(`/assets/${encodeURIComponent(assetId)}`, { via_asset_id: viaAssetId }),
    undefined,
    signal,
  )
}

/** How many assets one ancestry read may name; the backend refuses more with a 400. */
export const MAX_ANCESTRY_ASSETS = 50

/**
 * The folders above each of `assetIds`, root first, in one call — the tree each asset is read
 * through, or `workspaceId`'s when one is named. An asset the caller cannot read, or that the named
 * workspace does not hold, is absent from the answer rather than failing it. `reaches_root: false`
 * means the chain stops below a folder the caller may not see.
 */
export function listAssetAncestors(
  assetIds: readonly string[],
  workspaceId?: string | null,
  signal?: AbortSignal,
): Promise<AssetAncestryEntry[]> {
  return api.get<AssetAncestryEntry[]>(
    withQuery('/assets/ancestors', { asset_ids: assetIds.join(','), workspace_id: workspaceId }),
    undefined,
    signal,
  )
}

/**
 * Change what a customer wrote about an asset — and which still it shows
 * (`thumbnail_asset_file_id`). A field left out is left as it is. 409 when the handle is already
 * the organization's.
 */
export function updateAsset(assetId: string, request: UpdateAssetRequest): Promise<AssetView> {
  return api.patch<AssetView>(`/assets/${encodeURIComponent(assetId)}`, request)
}

/**
 * A free handle derived from the asset's name. Nothing is written: `updateAsset` stores the one
 * the customer keeps. 409 when every candidate is taken.
 */
export function generateAssetSlug(assetId: string): Promise<AssetSlugSuggestion> {
  return api.post<AssetSlugSuggestion>(`/assets/${encodeURIComponent(assetId)}/slug`)
}

/**
 * Move a selection into another folder of the same workspace, or to its root. All of it moves or
 * none of it does; 409 when the destination is inside a folder being moved.
 */
export function moveAssets(request: MoveAssetsRequest): Promise<AssetsMoved> {
  return api.post<AssetsMoved>('/assets/moves', request)
}

/** Put one asset in the trash, and everything under it when it is a folder. */
export function deleteAsset(assetId: string): Promise<AssetDeletionEntry> {
  return api.del<AssetDeletionEntry>(`/assets/${encodeURIComponent(assetId)}`)
}

/** Put a selection of one workspace in its trash: one deletion per asset named. */
export function deleteAssets(request: DeleteAssetsRequest): Promise<AssetsDeleted> {
  return api.post<AssetsDeleted>('/assets/deletions', request)
}

/**
 * What deleting a selection would cover, folder subtrees included, and where those assets are
 * used. `inventory_complete: false` means a publisher could not be asked — a screen says it could
 * not check rather than show an empty list as "used nowhere".
 */
export function previewAssetDeletion(request: PreviewAssetDeletionRequest): Promise<AssetDeletionPreview> {
  return api.post<AssetDeletionPreview>('/assets/deletions/preview', request)
}
